import axios from 'axios';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, createRef } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { Redirect, withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Container } from 'reactstrap';

import ContentPopups from '../../components/ad/ContentPopups';
import MainBanner from '../../components/ad/MainBanner';
import CategoryGrid from '../../components/category/CategoryGrid';
import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import ScrollReset from '../../components/common/ScrollReset';
import WysiwygContent from '../../components/common/WysiwygContent';
import AlternateLink from '../../components/head/AlternateLink';
import CanonicalLink from '../../components/head/CanonicalLink';
import GenericMeta from '../../components/head/GenericMeta';
import RobotsMeta from '../../components/head/RobotsMeta';
import SEODescription from '../../components/head/SEODescription';
import SEOTitle from '../../components/head/SEOTitle';
import Page from '../../components/layout/Page';
import SidebarLayout from '../../components/layout/SidebarLayout';
import ContentForState from '../../components/loader/ContentForState';
import NavigationCategorySlider from '../../components/navigation/NavigationCategorySlider';
import NavigationSimpleCategoryList from '../../components/navigation/NavigationSimpleCategoryList';
import MainProductList from '../../components/product-list/MainProductList';
import ProductPdf from '../../components/product-pdf/ProductPdf';
import ProductTabs from '../../components/product/ProductTabs';
import DefaultSidebar from '../../components/sidebar/DefaultSidebar';
import PageSkeleton from '../../components/skeleton/PageSkeleton';
import SidebarSkeleton from '../../components/skeleton/SidebarSkeleton';
import SuitableProductFinder from '../../components/suitable-product/SuitableProductFinder';
import prepareBanner from '../../hooks/banner/prepareBanner';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AccountStore from '../../store/AccountStore';
import AdStore from '../../store/AdStore';
import CategoryStore from '../../store/CategoryStore';
import ConfigStore from '../../store/ConfigStore';
import ManufacturerStore from '../../store/ManufacturerStore';
import SectionStore from '../../store/SectionStore';
import UIStore from '../../store/UIStore';
import AdZones from '../../types/AdZones';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import ProductPdfType from '../../types/ProductPdfType';
import RequestState from '../../types/RequestState';
import {
  appendCategoryManufacturerPathSuffix,
  getCategoryManufacturerRouteState,
  resolveManufacturerFromParam,
} from '../../util/categoryManufacturerRouting';
import { consoleErrorUnlessFiltered } from '../../util/sentryFilters';
import { parse, stringify } from '../../util/queryString';
import { pathFragmentMatchesLocation } from '../../util/url';
import {
  buildCategoryDisplayName,
  buildCategorySeoTitle,
  shouldDisableCategorySeoTitleSuffix,
} from './categorySeoTitle';

const MANUFACTURER_FILTER_KEY = 'manufacturers';

@observer
export class CategoryPage extends Component {
  constructor(props) {
    super(props);

    this.yotpoTimeout = null;
    this.ensureCorrectActiveCategorySet();
    this.ensureManufacturerRouteValidationReady();

    this.state = {
      productCount: 0,
    };

    this.productListRef = createRef();
  }

  componentDidMount() {
    // Another component from the previous DOM may fire its componentWillUnmount
    // and clear the category in between the initial getDerivedStateFromProps
    // and componentDidMount. To compensate for that, check whether the category
    // needs to be loaded again.
    this.ensureCorrectActiveCategorySet();
    this.ensureManufacturerRouteValidationReady();
    this.maybeLoadAds();
  }

  componentDidUpdate() {
    const { configStore } = this.props;
    this.ensureCorrectActiveCategorySet();
    this.ensureManufacturerRouteValidationReady();
    if (configStore.integrations.yotpo.enabled && window && window.yotpo) {
      this.yotpoTimeout = setTimeout(() => window.yotpo.refreshWidgets(), 800);
    }
  }

  componentWillUnmount() {
    const { categoryStore, match } = this.props;
    // Clean-up active category, if it was set by this component
    if (
      categoryStore.activeCategory &&
      categoryStore.activeCategory.id === Number(match.params.id)
    ) {
      categoryStore.setActiveCategory(null);
    }
    this.state = {};
    this.yotpoTimeout && clearTimeout(this.yotpoTimeout);
  }

  updateProductCount = (count) => {
    /* TODO refactor is required to handle async calls better */
    if (
      count !== this.state.productCount &&
      this.state.productCount !== undefined
    ) {
      this.setState(() => ({
        productCount: count,
      }));
    }
  };

  ensureCorrectActiveCategorySet = () => {
    const { categoryStore, match } = this.props;
    if (
      !categoryStore.activeCategory ||
      categoryStore.activeCategory.id !== Number(match.params.id)
    ) {
      this.updateActiveCategory();
    }
  };

  ensureManufacturerRouteValidationReady = () => {
    const manufacturerParam = this.getManufacturerParam();
    const { manufacturerStore } = this.props;

    if (
      !manufacturerParam ||
      !manufacturerStore ||
      manufacturerStore.isLoaded ||
      manufacturerStore.isLoading
    ) {
      return;
    }

    manufacturerStore
      .loadManufacturers({ excludeContent: true })
      .catch(() => null);
  };

  getAdParams = () => {
    const { categoryStore } = this.props;

    return categoryStore.activeCategory
      ? { category: categoryStore.activeCategory.id }
      : null;
  };

  shouldHaveAds = () => {
    const { adStore } = this.props;
    return adStore.categoryPageAds.length > 0;
  };

  shouldHaveMainAds = () => {
    const { adStore } = this.props;
    return adStore.categoryPageAds.indexOf(AdZones.MAIN_BANNER) !== -1;
  };

  shouldHaveSidebarAds = () => {
    const { adStore } = this.props;
    return adStore.categoryPageAds.indexOf(AdZones.SIDEBAR_BANNER) !== -1;
  };

  shouldHavePopup = () => {
    const { adStore } = this.props;
    return adStore.categoryPageAds.indexOf(AdZones.POPUP_CONTENT) !== -1;
  };

  maybeLoadAds = () => {
    const { adStore } = this.props;

    prepareBanner({
      adStore,
      searchParameters: this.getAdParams(),
      path: '/category',
      enabled: this.shouldHaveAds(),
    });
  };

  updateActiveCategory = () => {
    const { categoryStore, uiStore, match } = this.props;
    categoryStore.setActiveCategory(match.params.id);

    // If active category did not get set, return 404 to user, because it means it was not found.
    if (!categoryStore.activeCategory) {
      categoryStore.setSingleStateError(match.params.id, {
        response: { status: 404 },
      });
      return;
    }

    if (
      categoryStore.categoryStates.get(match.params.id) !==
        RequestState.LOADING &&
      categoryStore.activeCategory.description_html === null
    ) {
      categoryStore
        .loadCategory(match.params.id)
        .then(() => {
          categoryStore.activeCategory?.name &&
            uiStore.placeholders.productList.setHeight(
              categoryStore.activeCategory.name
            );
        })
        .catch((e) => {
          if (!axios.isCancel(e)) {
            consoleErrorUnlessFiltered(e);
          }
        });
    }
    this.maybeLoadAds();
  };

  getManufacturerPathSuffix = (
    manufacturerParam = this.getEffectiveManufacturerParam()
  ) => (manufacturerParam ? `/${manufacturerParam}/` : '');

  getManufacturerParam = () => this.props.match?.params?.manufacturer;

  getManufacturerFilterValuesFromQuery = () => {
    const queryParams = parse(this.props.location.search);
    const manufacturers = queryParams.f?.[MANUFACTURER_FILTER_KEY];

    if (Array.isArray(manufacturers)) {
      return manufacturers;
    }

    return manufacturers ? [manufacturers] : [];
  };

  getCategoryManufacturerRouteState = () =>
    getCategoryManufacturerRouteState({
      manufacturerStore: this.props.manufacturerStore,
      manufacturerParam: this.getManufacturerParam(),
      queryManufacturerValues: this.getManufacturerFilterValuesFromQuery(),
    });

  getEffectiveManufacturerParam = () =>
    this.getCategoryManufacturerRouteState().effectiveManufacturerParam;

  getManufacturerFromParam = (
    manufacturerParam = this.getManufacturerParam()
  ) =>
    resolveManufacturerFromParam(
      this.props.manufacturerStore,
      manufacturerParam
    );

  getCategorySeoTitle = () => {
    const { categoryStore, configStore } = this.props;
    return buildCategorySeoTitle({
      activeCategory: categoryStore.activeCategory,
      configStore,
      manufacturerStore: this.props.manufacturerStore,
      manufacturerParam: this.getEffectiveManufacturerParam(),
    });
  };

  shouldDisableSeoTitleSuffix = () => {
    return shouldDisableCategorySeoTitleSuffix({
      activeCategory: this.props.categoryStore.activeCategory,
      configStore: this.props.configStore,
      manufacturerStore: this.props.manufacturerStore,
      manufacturerParam: this.getEffectiveManufacturerParam(),
    });
  };

  getPageTitle = () => {
    return buildCategoryDisplayName({
      activeCategory: this.props.categoryStore.activeCategory,
      manufacturerStore: this.props.manufacturerStore,
      manufacturerParam: this.getEffectiveManufacturerParam(),
    });
  };

  appendPathSuffix = (basePath, suffix) => {
    if (!suffix) {
      return basePath;
    }

    const normalizedBase = basePath.endsWith('/')
      ? basePath.slice(0, -1)
      : basePath;
    const normalizedSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
    return `${normalizedBase}${normalizedSuffix}`;
  };

  getCanonicalCategoryPath = () => {
    const { categoryStore, routeService } = this.props;
    const basePath = categoryStore.activeCategory.path;
    const pathWithManufacturer = appendCategoryManufacturerPathSuffix(
      basePath,
      this.getEffectiveManufacturerParam()
    );

    return routeService.getCanonicalCategoryPath(
      categoryStore.activeCategory,
      pathWithManufacturer
    );
  };

  shouldNormalizeManufacturerPath = () => {
    const {
      shouldRemoveManufacturerPath,
      shouldStripDuplicateQueryManufacturers,
    } = this.getCategoryManufacturerRouteState();

    return (
      Boolean(this.getManufacturerParam()) &&
      (shouldRemoveManufacturerPath || shouldStripDuplicateQueryManufacturers)
    );
  };

  getManufacturerPathRedirect = () => {
    const { categoryStore, location, routeService } = this.props;
    const queryParams = parse(location.search);
    const filters = queryParams.f || {};
    const { cleanedQueryManufacturerValues, shouldKeepManufacturerPath } =
      this.getCategoryManufacturerRouteState();
    const nextFilters = {
      ...filters,
    };

    if (cleanedQueryManufacturerValues.length > 0) {
      nextFilters[MANUFACTURER_FILTER_KEY] = cleanedQueryManufacturerValues;
    } else {
      delete nextFilters[MANUFACTURER_FILTER_KEY];
    }

    const nextQueryParams = {
      ...queryParams,
      ...(Object.keys(nextFilters).length > 0 ? { f: nextFilters } : {}),
    };

    if (Object.keys(nextFilters).length === 0) {
      delete nextQueryParams.f;
    }

    return {
      pathname: shouldKeepManufacturerPath
        ? appendCategoryManufacturerPathSuffix(
            routeService.getCategoryPath(categoryStore.activeCategory),
            this.getManufacturerParam()
          )
        : routeService.getCategoryPath(categoryStore.activeCategory),
      search: stringify(nextQueryParams),
      hash: location.hash,
    };
  };

  getBreadcrumbsPath = () => {
    const { categoryStore, routeService } = this.props;

    if (!categoryStore.activeCategory) {
      return [];
    }

    return routeService.transformBreadCrumbs(
      categoryStore.activeCategory.breadcrumbs
    );
  };

  pdfCatalogEnabled = () => {
    const { accountStore, configStore } = this.props;
    const { showPdfButton } = configStore.productList;

    if (showPdfButton === '0') {
      return false;
    }
    if (
      showPdfButton === '1' ||
      (showPdfButton === '2' && accountStore.loggedIn)
    ) {
      return true;
    }
    return showPdfButton === '3' && accountStore.isRetailer;
  };

  renderProductPdfButton = () => {
    const { categoryStore, configStore } = this.props;
    const { productCount } = this.state;

    const isMainCategory = categoryStore.activeCategory.ancestors.length === 0;
    let requireEmail = true;
    const children = this.getChildren();
    const hasChildren = children.length > 0;

    const hasProductList =
      !isMainCategory ||
      (isMainCategory &&
        (configStore.mainCategoryPage.showMainProductList || !hasChildren));

    if (
      hasProductList &&
      productCount <= configStore.productList.productCountForEmail
    ) {
      requireEmail = false;
    }

    return (
      <ProductPdf
        activeId={categoryStore.activeCategory.id.toString()}
        pdfType={ProductPdfType.CATEGORY}
        requireEmail={requireEmail}
      />
    );
  };

  getChildren = () => {
    const { categoryStore, sectionStore } = this.props;
    return sectionStore.activeSection
      ? categoryStore.activeCategory.getNonBlockedChildren(
          sectionStore.activeSection.blockedCategoriesDict
        )
      : categoryStore.activeCategory.children;
  };

  isMainCategory = () => {
    const { categoryStore } = this.props;
    return categoryStore.activeCategory.ancestors.length === 0;
  };

  getCategoryNavigation = () => {
    const { configStore, uiStore } = this.props;

    const children = this.getChildren();
    const hasChildren = children.length > 0;

    if (
      (!configStore.mainCategoryPage.showCategoryGrid &&
        !configStore.navigation.common.showCategorySlider) ||
      !hasChildren
    ) {
      return null;
    }

    if (
      configStore.mainCategoryPage.showCategoryGrid &&
      this.isMainCategory() &&
      !uiStore.isMobile
    ) {
      return <CategoryGrid categories={children} />;
    }

    if (configStore.navigation.common.showCategorySlider) {
      return <NavigationCategorySlider categories={children} />;
    }

    return null;
  };

  getCategoryContent = () => {
    const listId = 'category_list';
    const listName = 'Category List';
    const { match, sectionStore, categoryStore, configStore } = this.props;

    const fixedParams = {
      categories: [match.params.id],
      cross_categories: [],
    };
    if (sectionStore.activeSection) {
      fixedParams.sections = [sectionStore.activeSection.id];
    }
    const showCategoryFilter =
      categoryStore.activeCategory.show_category_filter;

    if (
      configStore.suitableProducts.enabledOnCategories.indexOf(
        categoryStore.activeCategory.id
      ) !== -1
    ) {
      return (
        <SuitableProductFinder
          categoryId={Number(match.params.id)}
          listId={listId}
          listName={listName}
        />
      );
    } else if (this.isMainCategory()) {
      let hasChildren;
      const children = this.getChildren();
      hasChildren = children.length > 0;

      let maybeProductList;
      if (configStore.mainCategoryPage.showMainProductList || !hasChildren) {
        maybeProductList = (
          <MainProductList
            productListRef={this.productListRef}
            fixedParams={fixedParams}
            listId={listId}
            name={listName}
            showCategoryFilter={showCategoryFilter}
            updateProductCount={this.updateProductCount}
            isCategoryPage
          />
        );
      } else if (configStore.mainCategoryPage.showProductTabs) {
        maybeProductList = (
          <ProductTabs
            productListRef={this.productListRef}
            searchParams={{
              ...fixedParams,
            }}
            updateProductCount={this.updateProductCount}
          />
        );
      }

      return maybeProductList;
    } else {
      return (
        <MainProductList
          productListRef={this.productListRef}
          fixedParams={fixedParams}
          listId={listId}
          name={listName}
          showCategoryFilter={showCategoryFilter}
          updateProductCount={this.updateProductCount}
          isCategoryPage
        />
      );
    }
  };

  renderLoadedContent = (mainBannerWithinContent, mainBanner) => {
    const { categoryStore, configStore, uiStore } = this.props;
    const { productCount, categorySliderScrollState } = this.state;

    if (!categoryStore.activeCategory) {
      return null;
    }

    if (this.productListRef?.current) {
      uiStore.placeholders.productList.updateHeight({
        page: categoryStore.activeCategory.name,
        height: this.productListRef?.current.clientHeight,
      });
    }

    return (
      <>
        {!categoryStore.activeCategory.isIndexableByRobots && (
          <RobotsMeta noindex nofollow />
        )}
        <SEOTitle
          title={this.getCategorySeoTitle()}
          noSuffix={this.shouldDisableSeoTitleSuffix()}
        />
        {categoryStore.activeCategory.SEO_description && (
          <SEODescription
            content={categoryStore.activeCategory.SEO_description}
          />
        )}
        {!uiStore.isMobile && categoryStore.activeCategory.image && (
          <GenericMeta
            property="og:image"
            content={categoryStore.activeCategory.image}
          />
        )}
        {uiStore.isMobile && categoryStore.activeCategory.mobile_image && (
          <GenericMeta
            property="og:image"
            content={categoryStore.activeCategory.mobile_image}
          />
        )}
        <CanonicalLink path={this.getCanonicalCategoryPath()} />
        <AlternateLink
          isCategoryPage
          model={categoryStore.activeCategory}
          pathSuffix={this.getManufacturerPathSuffix()}
        />
        {mainBannerWithinContent && mainBanner}
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <section
          className="CategoryPage__main-content"
          aria-label={this.props.intl.formatMessage(
            globalTranslations.pageContentRegionLabel
          )}
        >
          <PageTitle>{this.getPageTitle()}</PageTitle>
          {categoryStore.activeCategory.description_html && (
            <div className="CategoryPage__category-content">
              <WysiwygContent
                html={categoryStore.activeCategory.description_html}
              />
            </div>
          )}
          {this.getCategoryNavigation()}
          {this.pdfCatalogEnabled() && this.renderProductPdfButton()}
        </section>
        {this.getCategoryContent()}
        <div className="CategoryPage__category-footer-content">
          <WysiwygContent
            html={categoryStore.activeCategory.footer_description_html}
          />
        </div>
        {categoryStore.activeCategory.children.length > 0 &&
          productCount > 0 &&
          configStore.navigation.common.showCategoryList && (
            <NavigationSimpleCategoryList
              categories={categoryStore.activeCategory.children}
            />
          )}
        {this.shouldHavePopup() && (
          <ContentPopups searchParams={this.getAdParams()} />
        )}
      </>
    );
  };

  /* TODO The component requires some refactoring.
   *   Maybe the logic of what listing / content could be defined in the backend ex. per category.
   *   Also some configurations should be renamed for better description. */

  render() {
    const {
      categoryStore,
      configStore,
      sectionStore,
      routeService,
      manufacturerStore,
      uiStore,
      match,
      location,
    } = this.props;

    const manufacturerParam = this.getManufacturerParam();
    const hasManufacturerParam = Boolean(manufacturerParam);

    const existingManufacturer =
      hasManufacturerParam && this.getManufacturerFromParam(manufacturerParam);

    if (categoryStore.activeCategory === null) {
      return <Redirect to={routeService.getPath(Paths.NotFoundPage)} />;
    }

    const maybeRedirectTo = `${routeService.getCategoryPath(
      categoryStore.activeCategory
    )}${location.search}${location.hash}`;

    if (this.shouldNormalizeManufacturerPath()) {
      return <Redirect to={this.getManufacturerPathRedirect()} />;
    }

    // Fix the URL when section store is missing activeSection
    if (configStore.activateSections && sectionStore.activeSection === null) {
      // Unless the category has no section
      if (
        routeService.getCategoryPath(categoryStore.activeCategory) !==
        categoryStore.activeCategory.path
      ) {
        return <Redirect to={maybeRedirectTo} />;
      }
    }

    // Fix the URL if it contains unexpected parts
    if (
      categoryStore.activeCategory &&
      String(categoryStore.activeCategory.id) === match.params.id &&
      (!hasManufacturerParam ||
        (manufacturerStore.isLoaded && !existingManufacturer)) &&
      !pathFragmentMatchesLocation(
        categoryStore.activeCategory.path,
        location.pathname
      )
    ) {
      return <Redirect to={maybeRedirectTo} />;
    }

    const id = match.params.id;

    const mainBanner = this.shouldHaveMainAds() ? (
      <MainBanner searchParams={this.getAdParams()} />
    ) : null;
    const mainBannerWithinContent = configStore.banner.mainBanner.withinContent;

    return (
      <Page name={CommonPage.CATEGORY} className="CategoryPage">
        <ScrollReset key={id} />
        {!mainBannerWithinContent && mainBanner}
        <Container className="CategoryPage__container">
          <SidebarLayout
            sidebarPlaceHolder={!uiStore.isMobile ? <SidebarSkeleton /> : null}
            sidebar={
              <DefaultSidebar
                bannerSearchParams={
                  this.shouldHaveSidebarAds() ? this.getAdParams() : null
                }
              />
            }
            content={
              <ContentForState
                state={categoryStore.categoryStates.get(id)}
                error={categoryStore.lastError}
                forPlaceHolder={<PageSkeleton />}
                forLoaded={() =>
                  this.renderLoadedContent(mainBannerWithinContent, mainBanner)
                }
              />
            }
          />
        </Container>
      </Page>
    );
  }
}

CategoryPage.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  adStore: modelOf(AdStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  manufacturerStore: modelOf(ManufacturerStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  match: RouterPropTypes.match.isRequired,
  location: RouterPropTypes.location.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject(
    'accountStore',
    'adStore',
    'categoryStore',
    'configStore',
    'manufacturerStore',
    'sectionStore',
    'uiStore',
    'routeService'
  )(withRouter(CategoryPage))
);
